import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import ModalComponent from "src/core/components/modal/modal.component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear } from "@fortawesome/free-solid-svg-icons";
import SkeletonComponent from "src/shared/custom-spinner/skeleton.component";
import {
  getDetectionRangeBySupplierName,
  getOperationModes,
} from "src/core/api/services/command-module/general-config.service";
import {
  getZoneGeneralConfigurationParameters,
  setZoneGeneralConfiguration,
} from "src/core/api/services/command-module/zone.service";
import { displayToast } from "src/shared/helpers/toaster.helper";
import {
  LIGHTING_FIELD_CONFIG,
  SENSOR_THRESHOLD_CONFIG,
  sanitizeNumericInput,
  useGeneralConfigurationValidation,
} from "../../general-configuration/hooks/use-general-configuration-validation.hook";
import { LightingConfigurationSection } from "../../general-configuration/components/lighting-configuration.component";
import { SensorConfigurationSection } from "../../general-configuration/components/sensor-configuration.component";
import { ZoneConfigurationHistoryTable } from "./zone-configuration-history-table.component";
import { useZoneConfigurationAck } from "./zone-configuration-ack-provider.component";
import type {
  LightingValues,
  SensorThresholdField,
  SensorValues,
  Zone,
} from "../../components/types";

type ZoneGeneralConfigurationModalProps = {
  zone: Zone;
  vendor?: string;
  assetCode: string | null;
  onClose: () => void;
  onApplyStarted: (zoneId: number) => void;
  onApplyFailed: (zoneId: number) => void;
  onSaved: () => void;
};

export function ZoneGeneralConfigurationModal({
  zone,
  vendor,
  assetCode,
  onClose,
  onApplyStarted,
  onApplyFailed,
  onSaved,
}: ZoneGeneralConfigurationModalProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const { historyRefetchTriggers, setHistoryOpenZoneId } = useZoneConfigurationAck();
  const [operationModeId, setOperationModeId] = useState<number | null>(null);
  const [lightingValues, setLightingValues] = useState<LightingValues>({
    rampUpTime: null,
    rampDownTime: null,
    dimLevel: null,
    brightLevel: null,
    holdTime: null,
  });
  const [sensorValues, setSensorValues] = useState<SensorValues>({
    detectionRangeId: null,
    lowerThreshold: null,
    upperThreshold: null,
    isPhotoSensing: false,
  });

  const { data: zoneConfigurationResponse, isLoading: isLoadingZoneConfiguration } = useQuery({
    queryKey: ["getZoneGeneralConfigurationParameters", zone.id],
    queryFn: ({ signal }) => getZoneGeneralConfigurationParameters(zone.id, signal),
    enabled: zone.id != null,
  });

  const { data: operationModesResponse } = useQuery({
    queryKey: ["getOperationModes"],
    queryFn: ({ signal }) => getOperationModes(signal),
  });
  const { data: detectionRangesResponse } = useQuery({
    queryKey: ["getDetectionRangeBySupplierName", vendor],
    queryFn: ({ signal }) => getDetectionRangeBySupplierName(vendor || "", signal),
    enabled: !!vendor,
  });

  const operationModeOptions = operationModesResponse?.data || [];
  const detectionRangeOptions = detectionRangesResponse?.data || [];
  const operationMode = operationModeOptions.find(
    (mode: { id: number; name: string }) => mode.id === operationModeId,
  )?.name ?? "";
  const { displayedErrors, validateForm } =
    useGeneralConfigurationValidation(lightingValues, sensorValues);

  useEffect(() => () => setHistoryOpenZoneId(null), [setHistoryOpenZoneId]);

  const handleOpenHistory = () => {
    setHistoryOpenZoneId(zone.id);
    setIsHistoryModalOpen(true);
  };

  const handleCloseHistory = () => {
    setHistoryOpenZoneId(null);
    setIsHistoryModalOpen(false);
  };

  useEffect(() => {
    const configuration = zoneConfigurationResponse?.data;
    if (!configuration) return;

    setOperationModeId(configuration.operationModeId ?? null);
    setLightingValues({
      rampUpTime: configuration.rampUpTime ?? null,
      rampDownTime: configuration.rampDownTime ?? null,
      dimLevel: configuration.dimLevel ?? null,
      brightLevel: configuration.brightLevel ?? null,
      holdTime: configuration.timeDelay ?? null,
    });
    setSensorValues({
      detectionRangeId: configuration.detectionRangeId ?? null,
      lowerThreshold: configuration.photoLowerThreshold ?? null,
      upperThreshold: configuration.photoUpperThreshold ?? null,
      isPhotoSensing: configuration.photoSensing ?? false,
    });
  }, [zoneConfigurationResponse]);

  const handleSave = async () => {
    if (!validateForm()) return;

    onApplyStarted(zone.id);
    setIsSaving(true);
    try {
      const response = await setZoneGeneralConfiguration({
        gatewayZoneId: zone.id,
        operationModeId,
        failsafeDelay: operationModeId === null ? null : 0,
        rampUpTime: lightingValues.rampUpTime,
        rampDownTime: lightingValues.rampDownTime,
        dimLevel: lightingValues.dimLevel,
        brightLevel: lightingValues.brightLevel,
        timeDelay: lightingValues.holdTime,
        detectionRangeId: sensorValues.detectionRangeId,
        photoLowerThreshold: sensorValues.lowerThreshold,
        photoUpperThreshold: sensorValues.upperThreshold,
        photoSensing: sensorValues.isPhotoSensing || null,
      });

      if (response?.status !== 201) {
        onApplyFailed(zone.id);
        // displayToast(response?.message || "Failed to save zone configuration.", "red");
        return;
      }

      // displayToast(response.message || "Zone configuration submitted successfully.", "green");
      onSaved();
      onClose();
    } catch (error: any) {
      onApplyFailed(zone.id);
      displayToast(
        error?.response?.data?.message || error?.message || "Failed to save zone configuration.",
        "red",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const actionConfig = [{
    label: isSaving ? "Saving..." : "Save Changes",
    event: handleSave,
    variant: "primary",
    disabled: isSaving,
  }];

  return (
    <>
    <ModalComponent
      showModal={!isHistoryModalOpen}
      handleClose={onClose}
      size="xl"
      headerTitle={
        <div>
          <div>Configure Zone</div>
          <div style={{ fontSize: "0.85rem", color: "#64748b", marginTop: "2px" }}>{`Zone: ${zone.name} (${zone.zoneId})`}</div>
        </div>
      }
      headerIcon={
        <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "3rem", height: "3rem", borderRadius: "1rem", background: "#f0f7ff" }}>
          <FontAwesomeIcon icon={faGear} style={{ fontSize: "1.4rem", color: "#1776d1" }} />
        </div>
      }
      actionConfig={actionConfig}
    >
      <div style={{ marginTop: "1rem" }}>
      <div className="configuration-dashboard zone-configuration-dashboard">
        <SkeletonComponent
          wrapperHeight="340px"
          skeletonHeight="50px"
          count={5}
          isLoading={isLoadingZoneConfiguration}
        >
          <LightingConfigurationSection
            operationModeOptions={operationModeOptions}
            operationMode={operationMode}
            onOperationModeChange={(modeName) => {
              const selectedMode = operationModeOptions.find(
                (mode: { id: number; name: string }) => mode.name === modeName,
              );
              setOperationModeId(selectedMode?.id ?? null);
            }}
            lightingValues={lightingValues}
            onLightingChange={(field, rawValue) => {
              const config = LIGHTING_FIELD_CONFIG[field];
              setLightingValues((current) => ({
                ...current,
                [field]: sanitizeNumericInput(rawValue, config.min, config.max),
              }));
            }}
            onLightingReset={(field) =>
              setLightingValues((current) => ({ ...current, [field]: 0 }))
            }
            errors={displayedErrors}
            showGatewayConfigActions={false}
            onViewHistory={handleOpenHistory}
          />
        </SkeletonComponent>
        <SkeletonComponent
          wrapperHeight="340px"
          skeletonHeight="50px"
          count={5}
          isLoading={isLoadingZoneConfiguration}
        >
          <SensorConfigurationSection
            detectionRangeOptions={detectionRangeOptions}
            sensorValues={sensorValues}
            onEnableDetectionRange={() => {
              const firstRange = detectionRangeOptions[0];
              if (firstRange) {
                setSensorValues((current) => ({ ...current, detectionRangeId: firstRange.id }));
              }
            }}
            onDetectionRangeChange={(detectionRangeId) =>
              setSensorValues((current) => ({ ...current, detectionRangeId }))
            }
            onTogglePhotoSensing={(isPhotoSensing) =>
              setSensorValues((current) => ({ ...current, isPhotoSensing }))
            }
            onThresholdChange={(field: SensorThresholdField, rawValue) => {
              const config = SENSOR_THRESHOLD_CONFIG[field];
              setSensorValues((current) => ({
                ...current,
                [field]: sanitizeNumericInput(rawValue, config.min, config.max),
              }));
            }}
            onThresholdReset={(field) =>
              setSensorValues((current) => ({ ...current, [field]: 0 }))
            }
            errors={displayedErrors}
          />
        </SkeletonComponent>
      </div>
      </div>
    </ModalComponent>
    <ZoneConfigurationHistoryTable
      isOpen={isHistoryModalOpen}
      handleClose={handleCloseHistory}
      zoneId={zone.id}
      refetchTrigger={historyRefetchTriggers[zone.id] ?? 0}
    />
    </>
  );
}